import { ReactNode, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { useLogoutSync } from '@crossnokaye/ux-common';

import { useAppConfig } from '@/hooks/use-app-config';
import { useViewOnlyState } from '@/hooks/use-view-only-state';

import { AuthContext, AuthContextValue } from './types';

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const {
    isLoading,
    isAuthenticated,
    error,
    user,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const { data: appConfig } = useAppConfig();

  const { isViewOnly, requestViewOnlyToken } = useViewOnlyState(
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect
  );

  const clientId = appConfig?.auth0?.clientId;

  // Set up logout synchronization across apps and tabs
  const { broadcastLogout } = useLogoutSync({
    clientId,
    isAuthenticated,
    isLoading,
    onLogoutDetected: () => {
      window.location.reload();
    },
  });

  const login = useCallback(
    () =>
      loginWithRedirect({
        authorizationParams: {
          prompt: 'login',
        },
        appState: {
          returnTo: window.location.pathname + window.location.search + window.location.hash,
        },
      }),
    [loginWithRedirect]
  );

  const logout = useCallback(() => {
    // Broadcast logout event for instant tab synchronization
    broadcastLogout();

    // Perform normal logout with redirect
    // Auth0 will automatically remove the auth0.is.authenticated cookie
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout, broadcastLogout]);

  const getAccessToken = useCallback(async () => {
    return await getAccessTokenSilently();
  }, [getAccessTokenSilently]);

  const contextValue: AuthContextValue = useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      error,
      user: user || undefined,
      login,
      logout,
      getAccessToken,
      isViewOnly,
      requestViewOnly: requestViewOnlyToken,
    }),
    [
      isLoading,
      isAuthenticated,
      error,
      user,
      login,
      logout,
      getAccessToken,
      isViewOnly,
      requestViewOnlyToken,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
